import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const slides = [
  {
    image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1600&q=80',
    title: 'Global trade at your fingertips',
    subtitle: 'At Alpha Import Export, we move commodities of every kind — from metals and chemicals to frozen foods and beverages — from verified producers straight to your dock.',
    cta: 'Browse catalog'
  },
  {
    image: 'https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1600&q=80',
    title: 'Industrial materials at contract volumes',
    subtitle: 'Alpha Import Export supplies aluminium, copper, ores and industrial chemicals with tiered pricing, clear MOQs and end-to-end logistics.',
    cta: 'Switch to wholesale'
  },
  {
    image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&w=1600&q=80',
    title: 'Food, beverage & fresh produce',
    subtitle: 'From frozen meat and agricultural produce to alcoholic and non-alcoholic beverages — every Alpha Import Export shipment ships with full traceable documentation.',
    cta: 'Who we are'
  }
]

export function HeroSlider({ onNavigate, onSegmentChange }) {
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length)
    }, 6000)
    return () => clearInterval(id)
  }, [])

  const prev = () => setCurrent((c) => (c - 1 + slides.length) % slides.length)
  const next = () => setCurrent((c) => (c + 1) % slides.length)

  const handleCta = (slide) => {
    if (slide.cta.includes('wholesale')) {
      onSegmentChange?.('wholesale')
      onNavigate('catalog')
    } else if (slide.cta.includes('Who')) {
      onNavigate('who-we-are')
    } else {
      onNavigate('catalog')
    }
  }

  return (
    <section className="relative h-[520px] w-full overflow-hidden sm:h-[620px]">
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className={`h-full w-full object-cover transition-transform duration-[8000ms] ease-linear ${
              i === current ? 'scale-100' : 'scale-110'
            }`}
          />
          <div className="absolute inset-0 bg-black/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/40 via-brand-navy/10 to-transparent" />
        </div>
      ))}

      <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-7xl">
          <div className="max-w-xl rounded-2xl border border-white/10 bg-brand-navy/85 p-7 shadow-2xl backdrop-blur-sm sm:p-9">
            {slides.map((slide, i) => (
              <div
                key={i}
                className={`${i === current ? 'block' : 'hidden'}`}
              >
                <span className="animate-fade-up mb-4 inline-block rounded-full border border-brand-gold/40 bg-brand-gold/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-50">
                  Alpha Import Export
                </span>
                <h1 className="animate-fade-up font-display text-4xl font-bold leading-tight text-[#FFFBE6] sm:text-5xl lg:text-6xl" style={{ animationDelay: '0.08s' }}>
                  {slide.title}
                </h1>
                <p className="animate-fade-up mt-4 text-lg text-[#FFF8D9]/95" style={{ animationDelay: '0.16s' }}>
                  {slide.subtitle}
                </p>
                <button
                  onClick={() => handleCta(slides[current])}
                  className="animate-fade-up btn-gold mt-8 inline-flex items-center gap-2" style={{ animationDelay: '0.24s' }}
                >
                  {slides[current].cta}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-2 rounded-full transition-all ${
              i === current ? 'w-8 bg-brand-gold' : 'w-2 bg-white/50'
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  )
}
